import {
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  useCallback,
  useRef,
  RefObject,
} from 'react';

// Types
import { TimelineLoadingState } from '@shared/types';
import {
  EmployeeActivity,
  EmployeeActivitiesResponse,
  ApiResponse,
} from '@/types';

// Actions
import { getEmployeeActivities } from '@/actions/client';

// Constants
import { ERROR_MESSAGES } from '@/constants/messages';

// Hooks
import { useFirebase } from '@/providers/firebase';

interface UseGetEmployeeActivitiesProps {
  employeeId: string;
  isInitialized: boolean;
  activities: EmployeeActivity[];
  setActivities: Dispatch<SetStateAction<EmployeeActivity[]>>;
}

interface UseGetEmployeeActivitiesReturn {
  activities: EmployeeActivity[];
  hasMore: boolean;
  loadingState: TimelineLoadingState;
  error: string | null;
  loadMore: () => Promise<void>;
  retry: () => Promise<void>;
  refetchActivities: () => Promise<void>;
}

/**
 * Merge activities and remove duplicates
 */
const mergeActivities = (
  existing: EmployeeActivity[],
  newActivities: EmployeeActivity[],
): EmployeeActivity[] => {
  if (!newActivities.length) return existing;
  if (!existing.length) return newActivities;

  const existingIds: Record<string, boolean> = {};
  for (const activity of existing) {
    existingIds[activity.id] = true;
  }

  const uniqueNew = newActivities.filter(
    (activity) => !existingIds[activity.id],
  );

  return [...existing, ...uniqueNew];
};

/**
 * Process API response and update state
 */
const processResponse = (
  response: ApiResponse<EmployeeActivitiesResponse>,
  isLoadMore: boolean,
  setActivities: Dispatch<SetStateAction<EmployeeActivity[]>>,
  setHasMore: Dispatch<SetStateAction<boolean>>,
  setCursor: Dispatch<SetStateAction<string>>,
  setError: Dispatch<SetStateAction<string | null>>,
  lastFailedTypeRef: RefObject<TimelineLoadingState | null>,
): void => {
  const {
    data: newActivities,
    meta: { pagination: { limit = 0, cursor: nextCursor = '' } = {} } = {},
  } = response.data!;

  if (isLoadMore) {
    setActivities((prev) => mergeActivities(prev, newActivities));
  } else {
    setActivities(newActivities);
  }

  setHasMore(newActivities.length >= limit && !!nextCursor);
  setCursor(nextCursor || '');
  setError(null);
  lastFailedTypeRef.current = null;
};

export const useGetEmployeeActivities = ({
  employeeId,
  isInitialized,
  activities,
  setActivities,
}: UseGetEmployeeActivitiesProps): UseGetEmployeeActivitiesReturn => {
  const [hasMore, setHasMore] = useState(true);
  const [loadingState, setLoadingState] = useState<TimelineLoadingState>(
    TimelineLoadingState.IDLE,
  );
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string>('');
  const { getToken } = useFirebase();

  const lastFailedTypeRef = useRef<TimelineLoadingState | null>(null);

  const fetchActivities = useCallback(
    async (isLoadMore = false, customCursor?: string) => {
      if (!employeeId) return;

      const loadingType = isLoadMore
        ? TimelineLoadingState.LOADING_MORE
        : TimelineLoadingState.INITIAL_LOADING;

      try {
        setLoadingState(loadingType);
        setError(null);

        const token = await getToken();
        const cursorParam = isLoadMore ? customCursor || cursor : undefined;

        const response = await getEmployeeActivities({
          username: employeeId,
          cursor: cursorParam,
          requestConfig: { token },
        });

        if (response?.error) {
          setError(response.error);
          lastFailedTypeRef.current = loadingType;
          return;
        }

        if (response?.data) {
          processResponse(
            response,
            isLoadMore,
            setActivities,
            setHasMore,
            setCursor,
            setError,
            lastFailedTypeRef,
          );
        }
      } catch {
        setError(ERROR_MESSAGES.FETCH_EMPLOYEE_ACTIVITIES);
        lastFailedTypeRef.current = loadingType;
      } finally {
        setLoadingState(TimelineLoadingState.IDLE);
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [employeeId, setActivities],
  );

  const loadMore = async (): Promise<void> => {
    // Only load more if we have more data, cursor exists, and not loading
    if (hasMore && cursor && loadingState === TimelineLoadingState.IDLE) {
      await fetchActivities(true, cursor);
    }
  };

  const retry = async (): Promise<void> => {
    const failedType = lastFailedTypeRef.current;
    await fetchActivities(failedType === TimelineLoadingState.LOADING_MORE);
  };

  const refetchActivities = async (): Promise<void> => {
    setCursor('');
    setHasMore(true);
    lastFailedTypeRef.current = null;

    await fetchActivities(false);
  };

  useEffect(() => {
    if (isInitialized && employeeId) {
      fetchActivities(false);
    }

    // We don't need to add fetchActivities to the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, employeeId]);

  return {
    activities,
    hasMore,
    loadingState,
    error,
    loadMore,
    retry,
    refetchActivities,
  };
};
